import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Container, Box, Typography, Card, CardContent, Button, CircularProgress,
  Alert, Grid, Chip, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, IconButton, Badge, FormControl, InputLabel, Select, MenuItem
} from '@mui/material';
import {
  SignalCellularAlt as OnlineIcon, SignalCellularOff as OfflineIcon,
  PauseCircle as PauseIcon, PlayCircle as ResumeIcon, OpenInNew as OpenIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import supportSocketService from '../services/supportSocketService';
import supportService from '../services/supportService';

const AgentDashboardPage = () => {
  const { t } = useTranslation(['support', 'common']);
  const navigate = useNavigate();
  const { user } = useAuth();
  const [socketConnected, setSocketConnected] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [pauseLoading, setPauseLoading] = useState(false);
  const [agent, setAgent] = useState(null);
  const [assignedTickets, setAssignedTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [sortBy, setSortBy] = useState('createdAt-desc');

  // Initialize Socket.io connection for agent
  useEffect(() => {
    const initSocket = async () => {
      try {
        await supportSocketService.connect();
        setSocketConnected(true);

        supportSocketService.on('agent_status_updated', (data) => {
          if (typeof data.isPaused === 'boolean') setIsPaused(data.isPaused);
          if (typeof data.isOnline === 'boolean') setSocketConnected(data.isOnline);
        });

        supportSocketService.on('ticket_assigned', () => {
          fetchAgentData();
        });
      } catch (err) {
        console.error('Socket connection failed:', err);
        setSocketConnected(false);
      }
    };

    initSocket();

    return () => {
      supportSocketService.off('agent_status_updated');
      supportSocketService.off('ticket_assigned');
    };
  }, []);

  // Fetch agent info and assigned tickets
  const fetchAgentData = useCallback(async () => {
    try {
      setLoading(true);
      const ticketsRes = await supportService.getAgentTickets();
      if (ticketsRes.success) {
        setAssignedTickets(ticketsRes.data?.tickets || []);
      }
    } catch (err) {
      setError(t('support:errors.fetchError'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchAgentData();
  }, [fetchAgentData]);

  const handlePauseToggle = async () => {
    setPauseLoading(true);
    try {
      const res = await supportService.toggleAgentPause();
      if (res?.data) {
        setIsPaused(res.data.isPaused);
        setSocketConnected(res.data.isOnline);
      }
    } catch (err) {
      setError(t('support:errors.fetchError'));
    } finally {
      setPauseLoading(false);
    }
  };

  const handleOpenTicket = (ticketId) => {
    window.open(`/support/tickets/${ticketId}`, '_blank');
  };

  // Filter and sort tickets
  const filteredAndSortedTickets = useMemo(() => {
    let filtered = assignedTickets;

    // Apply status filter
    if (statusFilter) {
      filtered = filtered.filter(ticket => ticket.status === statusFilter);
    }

    // Apply priority filter
    if (priorityFilter) {
      filtered = filtered.filter(ticket => ticket.priority === priorityFilter);
    }

    // Apply sorting
    const [sortField, sortDirection] = sortBy.split('-');
    filtered.sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];

      if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = bVal.toLowerCase();
      }

      let comparison = 0;
      if (aVal < bVal) comparison = -1;
      if (aVal > bVal) comparison = 1;

      return sortDirection === 'asc' ? comparison : -comparison;
    });

    return filtered;
  }, [assignedTickets, statusFilter, priorityFilter, sortBy]);

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 6, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header Card */}
      <Card sx={{ mb: 4, background: 'linear-gradient(45deg, #6200ee 30%, #03dac4 90%)' }}>
        <CardContent sx={{ color: 'white' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                <Typography variant="h5" fontWeight={700}>
                  {t('support:admin.agents.title')}
                </Typography>                
              </Box>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                {user?.name}
              </Typography>
            </Box>
            <Box sx={{ textAlign: 'right' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                {socketConnected ? (
                  <>
                    <OnlineIcon />
                    <Typography variant="body2">{t('support:admin.agents.online')}</Typography>
                  </>
                ) : (
                  <>
                    <OfflineIcon />
                    <Typography variant="body2">{t('support:admin.agents.offline')}</Typography>
                  </>
                )}
              </Box>
              <Button
                size="small"
                variant="outlined"
                startIcon={isPaused ? <ResumeIcon /> : <PauseIcon />}
                onClick={handlePauseToggle}
                disabled={pauseLoading}
                sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.5)' }}
              >
                {pauseLoading
                  ? '…'
                  : isPaused
                  ? t('support:admin.agents.resume')
                  : t('support:admin.agents.pause')}
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Status Info */}
      {!socketConnected && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          {t('support:admin.agents.offline')} - {t('common:connecting')}...
        </Alert>
      )}

      {/* Filters and Sorting */}
      <Card sx={{ mb: 3, p: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel id="status-filter-label">{t('support:ticket.status') || 'Status'}</InputLabel>
            <Select
              labelId="status-filter-label"
              value={statusFilter}
              label={t('support:ticket.status') || 'Status'}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <MenuItem value="">{t('common:all') || 'All'}</MenuItem>
              <MenuItem value="open">{t('support:status.open') || 'Open'}</MenuItem>
              <MenuItem value="in_progress">{t('support:status.in_progress') || 'In Progress'}</MenuItem>
              <MenuItem value="waiting_user">{t('support:status.waiting_user') || 'Waiting User'}</MenuItem>
              <MenuItem value="waiting_agent">{t('support:status.waiting_agent') || 'Waiting Agent'}</MenuItem>
              <MenuItem value="resolved">{t('support:status.resolved') || 'Resolved'}</MenuItem>
              <MenuItem value="closed">{t('support:status.closed') || 'Closed'}</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel id="priority-filter-label">{t('support:ticket.priority') || 'Priority'}</InputLabel>
            <Select
              labelId="priority-filter-label"
              value={priorityFilter}
              label={t('support:ticket.priority') || 'Priority'}
              onChange={(e) => setPriorityFilter(e.target.value)}
            >
              <MenuItem value="">{t('common:all') || 'All'}</MenuItem>
              <MenuItem value="low">{t('support:priority.low') || 'Low'}</MenuItem>
              <MenuItem value="medium">{t('support:priority.medium') || 'Medium'}</MenuItem>
              <MenuItem value="high">{t('support:priority.high') || 'High'}</MenuItem>
              <MenuItem value="urgent">{t('support:priority.urgent') || 'Urgent'}</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel id="sort-label">{t('common:sortBy') || 'Sort By'}</InputLabel>
            <Select
              labelId="sort-label"
              value={sortBy}
              label={t('common:sortBy') || 'Sort By'}
              onChange={(e) => setSortBy(e.target.value)}
            >
              <MenuItem value="createdAt-desc">{t('common:newest') || 'Newest'}</MenuItem>
              <MenuItem value="createdAt-asc">{t('common:oldest') || 'Oldest'}</MenuItem>
              <MenuItem value="priority-desc">{t('support:priority.highest') || 'Highest Priority'}</MenuItem>
              <MenuItem value="priority-asc">{t('support:priority.lowest') || 'Lowest Priority'}</MenuItem>
              <MenuItem value="updatedAt-desc">{t('common:recentlyUpdated') || 'Recently Updated'}</MenuItem>
            </Select>
          </FormControl>

          <IconButton
            onClick={fetchAgentData}
            size="small"
            sx={{
              marginLeft: 'auto',
              color: 'inherit',
              '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' }
            }}
            title="Refresh"
          >
            <RefreshIcon />
          </IconButton>
        </Box>
      </Card>

      {/* Assigned Tickets */}
      <Box>
        <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
          {t('support:admin.tickets.title')} ({filteredAndSortedTickets.length})
        </Typography>

        {filteredAndSortedTickets.length === 0 ? (
          <Alert severity="info">{t('support:admin.agents.noAgents')}</Alert>
        ) : (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: 'action.hover' }}>
                  <TableCell>{t('support:admin.tickets.title')}</TableCell>
                  <TableCell>{t('support:ticket.status')}</TableCell>
                  <TableCell>{t('support:ticket.priority')}</TableCell>
                  <TableCell>{t('support:ticket.category')}</TableCell>
                  <TableCell>{t('support:admin.tickets.created', 'Created')}</TableCell>
                  <TableCell>{t('common:action', 'Action')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredAndSortedTickets.map((ticket) => (
                  <TableRow key={ticket.id} hover>
                    <TableCell>
                      <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2" fontWeight={500}>
                            {ticket.subject}
                          </Typography>
                          {ticket.unreadMessageCount > 0 && (
                            <Badge badgeContent={ticket.unreadMessageCount} color="error">
                              <Box />
                            </Badge>
                          )}
                        </Box>
                        <Typography variant="caption" color="text.secondary">
                          #{ticket.ticketNumber}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={t(`support:status.${ticket.status}`)}
                        size="small"
                        color={
                          ticket.status === 'open'
                            ? 'error'
                            : ticket.status === 'in_progress'
                            ? 'warning'
                            : 'success'
                        }
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={t(`support:priority.${ticket.priority}`)}
                        size="small"
                        variant={ticket.priority === 'urgent' ? 'filled' : 'outlined'}
                      />
                    </TableCell>
                    <TableCell>{t(`support:categories.${ticket.category}`)}</TableCell>
                    <TableCell>{new Date(ticket.createdAt).toLocaleDateString()}</TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={() => handleOpenTicket(ticket.id)}
                        title={t('common:open')}
                      >
                        <OpenIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>
    </Container>
  );
};

export default AgentDashboardPage;
